import React, { useState, useMemo } from 'react';
import {
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import { DownloadButton } from './download';
import { useGetGRCReviewCycleSnapshotUrl } from 'src/api/hooks/useControlData';
import type { GetSnapshotCommandInput } from '@amzn/controllershiphub-typescript-client';
import { LEASE_TYPES } from 'src/constants/grc5253';

export enum GRCReviewCyclePhase {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export const QUARTERLY_MONTHS = ['March', 'June', 'September', 'December'];
export const START_YEAR = 2025;
export const AVAILABILITY_DAY_THRESHOLD = 15;

/**
 * Checks if a monthly report is available for a given month.
 * Monthly reports become available when the threshold day of the following month has been crossed.
 * @param monthIndex - Zero-based month index (0 = January, 11 = December)
 * @param year - The year of the month being checked
 * @param currentYear - The current year
 * @param currentMonthIndex - Zero-based current month index
 * @param currentDay - Current day of the month
 * @returns True if the monthly report is available
 */
export const isMonthlyReportAvailable = (
  monthIndex: number,
  year: number,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
): boolean => {
  // Calculate next month and its year (December wraps to January of next year)
  const nextMonthIndex = (monthIndex + 1) % 12;
  const nextMonthYear = monthIndex === 11 ? year + 1 : year;

  // If next month's year is in the past, report is available
  if (nextMonthYear < currentYear) return true;
  // If next month's year is in the future, report is not available
  if (nextMonthYear > currentYear) return false;
  // Next month is before current month in same year
  if (nextMonthIndex < currentMonthIndex) return true;
  // Next month is after current month in same year
  if (nextMonthIndex > currentMonthIndex) return false;

  // Same month: check if we've crossed the threshold day
  return currentDay >= AVAILABILITY_DAY_THRESHOLD;
};

/**
 * Checks if a quarterly report is available for a given month.
 * Quarterly reports become available when the month has ended.
 * @param monthIndex - Zero-based month index (must be 2, 5, 8, or 11 for quarterly months)
 * @param year - The year of the month being checked
 * @param currentYear - The current year
 * @param currentMonthIndex - Zero-based current month index
 * @returns True if the quarterly report is available
 */
export const isQuarterlyReportAvailable = (
  monthIndex: number,
  year: number,
  currentYear: number,
  currentMonthIndex: number
): boolean => {
  // Past years: all quarterly months available
  if (year < currentYear) return true;
  // Future years: no months available
  if (year > currentYear) return false;
  // Current year: month must be in the past (ended)
  return monthIndex < currentMonthIndex;
};

/**
 * Gets available years based on the selected phase.
 * For monthly: year is valid if January's report is available.
 * For quarterly: year is valid if March's report is available.
 * @param selectedPhase - The selected phase (monthly or quarterly)
 * @param startYear - The earliest year with available data
 * @param currentYear - The current year
 * @param currentMonthIndex - Zero-based current month index
 * @param currentDay - Current day of the month
 * @param threshold - Day threshold for monthly reports
 * @returns Array of available years
 */
export const getAvailableYears = (
  selectedPhase: GRCReviewCyclePhase,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
): number[] => {
  const years: number[] = [];

  for (let year = START_YEAR; year <= currentYear; year++) {
    if (selectedPhase === GRCReviewCyclePhase.QUARTERLY) {
      if (isQuarterlyReportAvailable(2, year, currentYear, currentMonthIndex)) {
        years.push(year);
      }
    } else {
      if (isMonthlyReportAvailable(0, year, currentYear, currentMonthIndex, currentDay)) {
        years.push(year);
      }
    }
  }

  return years;
};

/**
 * Gets available months based on the selected phase and year.
 * @param selectedPhase - The selected phase (monthly or quarterly)
 * @param selectedYear - The selected year
 * @param currentYear - The current year
 * @param currentMonthIndex - Zero-based current month index
 * @param currentDay - Current day of the month
 * @param threshold - Day threshold for monthly reports
 * @returns Array of available month names
 */
export const getAvailableMonths = (
  selectedPhase: GRCReviewCyclePhase,
  selectedYear: number,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
): string[] => {
  // Check if this is the start year (limited to Oct-Dec)
  const isStartYear = selectedYear === START_YEAR;

  // Start year only has Oct-Dec available (October = index 9)
  const startMonthIndex = isStartYear ? 9 : 0;

  if (selectedPhase === GRCReviewCyclePhase.QUARTERLY) {
    // Quarterly: only March, June, September, December
    return QUARTERLY_MONTHS.filter((month) => {
      const monthIndex = MONTHS.indexOf(month);
      if (monthIndex < startMonthIndex) return false;
      return isQuarterlyReportAvailable(monthIndex, selectedYear, currentYear, currentMonthIndex);
    });
  } else {
    // Monthly: all months where next month's threshold has been crossed
    return MONTHS.filter((_, monthIndex) => {
      if (monthIndex < startMonthIndex) return false;
      return isMonthlyReportAvailable(
        monthIndex,
        selectedYear,
        currentYear,
        currentMonthIndex,
        currentDay
      );
    });
  }
};

/**
 * Converts user-selected month/year to API month/year.
 * Reports are stored in the month after they are generated.
 * @param selectedMonth - The user-selected month name
 * @param selectedYear - The user-selected year as string
 * @returns Object with apiMonth and apiYear for the API call
 */
export const getApiMonthYear = (
  selectedMonth: string,
  selectedYear: string
): { apiMonth: string; apiYear: string } => {
  const monthIndex = MONTHS.indexOf(selectedMonth);
  // December wraps to January of next year
  if (monthIndex === 11) {
    return { apiMonth: '1', apiYear: String(parseInt(selectedYear) + 1) };
  }
  // Other months: increment by 1 (convert to 1-based and add 1)
  return { apiMonth: String(monthIndex + 2), apiYear: selectedYear };
};

export function GRC5253AWSReportSnapshotsForm() {
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [grcReviewCyclePhase, setGRCReviewCyclePhase] = useState<GRCReviewCyclePhase | undefined>(
    undefined
  );
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  const phaseOptions = [
    {
      label: 'Monthly',
      value: GRCReviewCyclePhase.MONTHLY,
    },
    {
      label: 'Quarterly',
      value: GRCReviewCyclePhase.QUARTERLY,
    },
  ];

  const availableYears = useMemo(() => {
    if (!grcReviewCyclePhase) return [];
    return getAvailableYears(grcReviewCyclePhase, currentYear, currentMonthIndex, currentDay).map(
      (y) => ({ label: String(y), value: String(y) })
    );
  }, [grcReviewCyclePhase, currentYear, currentMonthIndex, currentDay]);

  const availableMonths = useMemo(() => {
    if (!grcReviewCyclePhase || !year) return [];
    return getAvailableMonths(
      grcReviewCyclePhase,
      parseInt(year),
      currentYear,
      currentMonthIndex,
      currentDay
    ).map((m) => ({ label: m, value: m }));
  }, [grcReviewCyclePhase, year, currentYear, currentMonthIndex, currentDay]);

  const snapshotInput = useMemo((): GetSnapshotCommandInput => {
    if (!month || !year) {
      return { controlId: 'GRC5253', parameters: [] };
    }
    const { apiMonth, apiYear } = getApiMonthYear(month, year);
    return {
      controlId: 'GRC5253',
      parameters: [
        { key: 'phasename', value: grcReviewCyclePhase },
        { key: 'month', value: apiMonth },
        { key: 'year', value: apiYear },
        { key: 'leaseType', value: LEASE_TYPES.AWS },
      ],
    };
  }, [grcReviewCyclePhase, month, year]);

  const {
    refetch,
    isLoading,
    error: queryError,
  } = useGetGRCReviewCycleSnapshotUrl(snapshotInput, false);

  const isDownloadButtonDisabled = !(grcReviewCyclePhase && year && month);

  const handlePhaseChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setGRCReviewCyclePhase(detail.selectedOption.value as GRCReviewCyclePhase);
      setYear('');
      setMonth('');
    }
  };

  const handleYearChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setYear(detail.selectedOption.value);
      setMonth('');
    }
  };

  const handleMonthChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setMonth(detail.selectedOption.value);
    }
  };

  return (
    <Container header={<Header variant="h2">GRC Review Cycle Snapshot</Header>}>
      <Form
        errorText={error ? 'There was an error generating your snapshot.' : undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <DownloadButton
              getUrlFunction={() => {
                setError(false);
                setSuccess(false);
                return refetch()
                  .then((result) => {
                    if (result.data) {
                      setSuccess(true);
                    }
                    return result.data;
                  })
                  .catch((err) => {
                    setError(true);
                    throw err;
                  });
              }}
              setError={setError}
              getUrlFunctionProps={{}}
              buttonContent={isLoading ? 'Generating...' : 'Download Snapshot'}
              urlPath="presignedUrl"
              buttonDisabled={isDownloadButtonDisabled || isLoading}
            />
          </SpaceBetween>
        }
      >
        <SpaceBetween size="xl">
          <ColumnLayout columns={3}>
            <FormField label="Phase">
              <Select
                placeholder="Select Phase"
                selectedOption={
                  grcReviewCyclePhase
                    ? {
                        label:
                          phaseOptions.find((o) => o.value === grcReviewCyclePhase)?.label ?? '',
                        value: grcReviewCyclePhase,
                      }
                    : null
                }
                onChange={handlePhaseChange}
                options={phaseOptions}
              />
            </FormField>

            <FormField label="Year">
              <Select
                placeholder="Select Year"
                selectedOption={year ? { label: year, value: year } : null}
                onChange={handleYearChange}
                options={availableYears}
                disabled={!grcReviewCyclePhase}
              />
            </FormField>

            <FormField label="Month">
              <Select
                placeholder="Select Month"
                selectedOption={month ? { label: month, value: month } : null}
                onChange={handleMonthChange}
                options={availableMonths}
                disabled={!year}
              />
            </FormField>
          </ColumnLayout>

          {success && (
            <Alert
              dismissAriaLabel="Close alert"
              type="success"
              header="Success"
              onDismiss={() => setSuccess(false)}
            >
              Snapshot generated successfully and download started.
            </Alert>
          )}

          {(error || queryError) && (
            <Alert
              dismissAriaLabel="Close alert"
              type="error"
              header="Error"
              onDismiss={() => setError(false)}
            >
              {queryError?.message || 'There was an error generating your snapshot.'}
            </Alert>
          )}
        </SpaceBetween>
      </Form>
    </Container>
  );
}
